import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import API, { fileUrl } from '../utils/api';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C', V4 = '#2B0057';

const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const MegaphoneIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 0 1-5.8-1.6" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="0" y1="420" x2="210" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" /><circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const emptyForm = { title: '', message: '' };
const emptyButton = { text: '', link: '' };

const inputStyle = {
  width: '100%', background: 'var(--bg2)', border: '1.5px solid var(--border)',
  borderRadius: 10, color: 'var(--text)', fontFamily: "'Satoshi','DM Sans',sans-serif",
  fontSize: 14, padding: '11px 14px', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
};
const onFocus = e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; };
const onBlur  = e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; };

const AdminAnnouncement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [buttons, setButtons] = useState([{ ...emptyButton }]);
  const [editId, setEditId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const imageInputRef = useRef(null);

  const fetchAnnouncements = () => API.get('/announcements/admin/all').then(r => setAnnouncements(r.data));
  useEffect(() => { fetchAnnouncements(); }, []);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleButtonChange = (i, field, value) =>
    setButtons(prev => prev.map((b, idx) => idx === i ? { ...b, [field]: value } : b));
  const addButtonRow = () => setButtons(prev => [...prev, { ...emptyButton }]);
  const removeButtonRow = i => setButtons(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const handleImageSelect = e => {
    const f = e.target.files[0];
    if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
  };

  const resetForm = () => {
    setForm(emptyForm); setButtons([{ ...emptyButton }]); setEditId(null); setImageFile(null); setImagePreview('');
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleSubmit = async e => {
    e.preventDefault(); setLoading(true);
    const data = new FormData();
    data.append('title', form.title);
    data.append('message', form.message);
    data.append('buttons', JSON.stringify(buttons.filter(b => b.text.trim() && b.link.trim())));
    if (imageFile) data.append('image', imageFile);
    try {
      if (editId) { await API.put(`/announcements/${editId}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Announcement updated!'); }
      else        { await API.post('/announcements', data, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Announcement created!'); }
      setShowForm(false); resetForm();
      fetchAnnouncements();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleEdit = a => {
    setForm({
      title:   a.title   || '',
      message: a.message || '',
    });
    setButtons(a.buttons?.length ? a.buttons.map(b => ({ text: b.text, link: b.link })) : [{ ...emptyButton }]);
    setImageFile(null);
    setImagePreview(a.imageUrl ? fileUrl(a.imageUrl) : '');
    setEditId(a._id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this announcement?')) return;
    await API.delete(`/announcements/${id}`); toast.success('Deleted'); fetchAnnouncements();
  };

  const handleActivate = async a => {
    await API.put(`/announcements/${a._id}/activate`);
    toast.success('Published — now live on the homepage');
    fetchAnnouncements();
  };

  const handleDeactivate = async a => {
    await API.put(`/announcements/${a._id}/deactivate`);
    toast.success('Taken down from the homepage');
    fetchAnnouncements();
  };

  const fieldLabel = label => (
    <label style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 7 }}>{label}</label>
  );

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* HEADER */}
      <section style={{ padding: '120px 0 60px', position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/admin" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 24, transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Admin Dashboard
          </Link>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div className="section-label">ADMIN PANEL</div>
              <h1 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', fontFamily: "'Inter',sans-serif" }}>
                Announcement <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Banner</span>
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 6 }}>Publish, edit, or take down the homepage announcement. Only one can be live at a time.</p>
            </div>
            <button
              onClick={() => { setShowForm(!showForm); resetForm(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: showForm ? 'var(--card-solid)' : `linear-gradient(135deg,${V1},${V3})`,
                color: showForm ? 'var(--muted)' : '#fff',
                border: showForm ? '1.5px solid var(--border)' : 'none',
                fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 800, fontSize: 14,
                boxShadow: showForm ? 'none' : `0 4px 20px ${V1}44`,
                transition: 'all 0.2s',
              }}
            >
              {showForm ? <><XIcon /> Cancel</> : <><PlusIcon /> New Announcement</>}
            </button>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 40 }}>

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSubmit} style={{
              background: 'var(--card-solid)', border: `1px solid ${V1}44`,
              borderRadius: 20, padding: '32px', marginBottom: 40,
              boxShadow: `0 8px 40px ${V1}15`,
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 28, fontFamily: "'Inter',sans-serif" }}>
                {editId ? 'Edit Announcement' : 'New Announcement'}
              </h3>

              <div style={{ marginBottom: 16 }}>
                {fieldLabel('Title (internal label) *')}
                <input name="title" type="text" value={form.title} onChange={handleChange} required
                  placeholder="e.g. July Free Webinar"
                  style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <div style={{ marginBottom: 16 }}>
                {fieldLabel('Banner Message *')}
                <textarea name="message" value={form.message} onChange={handleChange} rows={3} required
                  placeholder="What visitors will see on the homepage banner"
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <div style={{ marginBottom: 24 }}>
                {fieldLabel('Buttons (optional — add as many as you like)')}
                {buttons.map((b, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginBottom: 10 }} className="form-grid">
                    <input type="text" value={b.text} onChange={e => handleButtonChange(i, 'text', e.target.value)}
                      placeholder="Button text, e.g. Register Now"
                      style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                    <input type="text" value={b.link} onChange={e => handleButtonChange(i, 'link', e.target.value)}
                      placeholder="https://... or /internal-page"
                      style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                    <button type="button" onClick={() => removeButtonRow(i)} disabled={buttons.length === 1} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 42, borderRadius: 10, border: '1.5px solid var(--border)',
                      background: 'transparent', color: 'var(--danger)',
                      cursor: buttons.length === 1 ? 'not-allowed' : 'pointer',
                      opacity: buttons.length === 1 ? 0.4 : 1,
                    }}>
                      <TrashIcon />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addButtonRow} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4,
                  padding: '8px 16px', borderRadius: 8, border: '1.5px dashed var(--border-strong)',
                  background: 'transparent', color: V2, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Satoshi','DM Sans',sans-serif",
                }}>
                  <PlusIcon /> Add Another Button
                </button>
              </div>

              <div style={{ marginBottom: 24 }}>
                {fieldLabel('Poster Image (optional — shown in full, any size/aspect ratio)')}
                <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => imageInputRef.current?.click()} style={{
                    padding: '10px 18px', borderRadius: 10, border: '1.5px solid var(--border-strong)',
                    background: 'transparent', color: V2, fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Satoshi','DM Sans',sans-serif",
                  }}>
                    {imagePreview ? 'Change Image' : 'Choose Image'}
                  </button>
                  {imagePreview && (
                    <img src={imagePreview} alt="Poster preview" style={{ height: 60, borderRadius: 8, border: '1px solid var(--border)', objectFit: 'cover' }} />
                  )}
                </div>
              </div>

              <button type="submit" disabled={loading} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '13px 28px', borderRadius: 12, border: 'none', cursor: loading ? 'wait' : 'pointer',
                background: `linear-gradient(135deg,${V1},${V3})`,
                color: '#fff', fontWeight: 800, fontSize: 15,
                fontFamily: "'Satoshi','DM Sans',sans-serif",
                boxShadow: `0 4px 20px ${V1}44`,
                opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s',
              }}>
                <CheckIcon />
                {loading ? 'Saving...' : editId ? 'Update Announcement' : 'Create Announcement'}
              </button>
            </form>
          )}

          {/* Announcements Table */}
          <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            {announcements.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', fontSize: 15 }}>No announcements yet. Create your first one!</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                      {['Title', 'Message', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{
                          padding: '14px 18px', textAlign: 'left',
                          color: 'var(--muted)', fontSize: 11.5, fontWeight: 800,
                          textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map((a, i) => (
                      <tr key={a._id}
                        style={{ borderBottom: i < announcements.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '15px 18px', fontWeight: 700, fontSize: 14, color: 'var(--text)', maxWidth: 180 }}>{a.title}</td>
                        <td style={{ padding: '15px 18px', color: 'var(--muted)', fontSize: 13, maxWidth: 320 }}>
                          {a.message?.length > 80 ? `${a.message.slice(0, 80)}…` : a.message}
                        </td>
                        <td style={{ padding: '15px 18px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 800,
                            background: a.isActive ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)',
                            color: a.isActive ? '#2ECC71' : 'var(--danger)',
                            border: `1px solid ${a.isActive ? 'rgba(46,204,113,0.28)' : 'rgba(240,65,85,0.28)'}`,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>
                            <MegaphoneIcon size={11} /> {a.isActive ? 'Live' : 'Not Live'}
                          </span>
                        </td>
                        <td style={{ padding: '15px 18px' }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {a.isActive ? (
                              <button onClick={() => handleDeactivate(a)} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '7px 13px', borderRadius: 8,
                                border: '1.5px solid rgba(245,166,35,0.35)', background: 'rgba(245,166,35,0.08)',
                                color: 'var(--warning)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                              }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'var(--warning)'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245,166,35,0.08)'; e.currentTarget.style.color = 'var(--warning)'; }}
                              >
                                Take Down
                              </button>
                            ) : (
                              <button onClick={() => handleActivate(a)} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '7px 13px', borderRadius: 8,
                                border: '1.5px solid rgba(46,204,113,0.35)', background: 'rgba(46,204,113,0.08)',
                                color: '#2ECC71', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                              }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#2ECC71'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.08)'; e.currentTarget.style.color = '#2ECC71'; }}
                              >
                                Publish
                              </button>
                            )}
                            <button onClick={() => handleEdit(a)} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '7px 13px', borderRadius: 8,
                              border: '1.5px solid var(--border-strong)', background: 'transparent',
                              color: V2, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                            }}
                              onMouseEnter={e => { e.currentTarget.style.background = V1; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = V1; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = V2; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                            >
                              <EditIcon /> Edit
                            </button>
                            <button onClick={() => handleDelete(a._id)} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '7px 13px', borderRadius: 8,
                              border: '1.5px solid rgba(240,65,85,0.3)', background: 'rgba(240,65,85,0.08)',
                              color: 'var(--danger)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                            }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'var(--danger)'; e.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(240,65,85,0.08)'; e.currentTarget.style.color = 'var(--danger)'; }}
                            >
                              <TrashIcon /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 700px) { .form-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
};

export default AdminAnnouncement;
